import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { PaginatedAdminUserResult } from "../contracts/PaginatedAdminUserResult";
import { PaginatedAdminFeedbackResult } from "../contracts/PaginatedAdminFeedbackResult";
import { ChangeUserLoginEmailRequest } from "../contracts/ChangeUserLoginEmailRequest";
import { ChangeUserLoginEmailResponse } from "../contracts/ChangeUserLoginEmailResponse";
import { SubscriptionStatusResponse } from "../../subscription/contracts/SubscriptionStatusResponse";
import { mapResponseToAdminUser } from "../mappers/AdminUserMapper";
import { mapResponseToAdminFeedbackEntry } from "../mappers/AdminFeedbackMapper";
import {
  mapResponseToReferralEntry,
  mapResponseToReferralPartner,
} from "../mappers/ReferralPartnerMapper";
import { AdminUser } from "../models/AdminUser";
import { AdminFeedbackEntry } from "../models/AdminFeedbackEntry";
import { ReferralEntry } from "../models/ReferralEntry";
import { ReferralPartner } from "../models/ReferralPartner";
import { AdminReferralPartnerDetailResponse } from "../contracts/AdminReferralPartnerDetailResponse";
import { AdminReferralPartnerResponse } from "../contracts/AdminReferralPartnerResponse";
import { CreateReferralPartnerRequest } from "../contracts/CreateReferralPartnerRequest";
import { PaginatedAdminReferralPartnerResult } from "../contracts/PaginatedAdminReferralPartnerResult";
import { SetCustomPriceRequest } from "../contracts/SetCustomPriceRequest";
import { AdminAcquisitionResponse } from "../contracts/AdminAcquisitionResponse";
import { mapResponseToAdminAcquisition } from "../mappers/AdminAcquisitionMapper";
import { AdminAcquisition } from "../models/AdminAcquisition";

const BASE = "/admin/users";

export interface FetchAdminUsersArgs {
  page?: number;
  pageSize?: number;
  search?: string;
}

export interface PaginatedAdminUsers {
  items: AdminUser[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export const fetchAdminUsers = createAsyncThunk<
  PaginatedAdminUsers,
  FetchAdminUsersArgs | void,
  { rejectValue: ApiErrorResponse }
>("admin/fetchUsers", async (args, { rejectWithValue }) => {
  try {
    const params: Record<string, string | number> = {
      page: (args && args.page) ?? 1,
      pageSize: (args && args.pageSize) ?? 20,
    };
    const search = args && args.search ? args.search.trim() : "";
    if (search) {
      params.search = search;
    }

    const response = await axiosInstance.get<PaginatedAdminUserResult>(BASE, { params });
    return {
      items: response.data.items.map(mapResponseToAdminUser),
      page: response.data.page,
      pageSize: response.data.pageSize,
      totalCount: response.data.totalCount,
      totalPages: response.data.totalPages,
      hasPreviousPage: response.data.hasPreviousPage,
      hasNextPage: response.data.hasNextPage,
    };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load users"));
  }
});

export interface SetComplimentaryArgs {
  instructorId: string;
  /** ISO UTC instant to grant free access until, or null to revoke it. */
  until: string | null;
}

export interface ComplimentaryUpdate {
  instructorId: string;
  complimentaryUntil: Date | null;
  subscriptionState: string;
}

/**
 * Grants or revokes complimentary free access for an instructor (beta testers) —
 * they stay Active without ever ordering or paying.
 */
export const setUserComplimentary = createAsyncThunk<
  ComplimentaryUpdate,
  SetComplimentaryArgs,
  { rejectValue: ApiErrorResponse }
>("admin/setComplimentary", async ({ instructorId, until }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<SubscriptionStatusResponse>(
      `${BASE}/${instructorId}/complimentary`,
      { until }
    );
    return {
      instructorId,
      complimentaryUntil: response.data.complimentaryUntil
        ? new Date(response.data.complimentaryUntil)
        : null,
      subscriptionState: response.data.state,
    };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to update complimentary access"));
  }
});

export interface ChangeUserLoginEmailArgs {
  instructorId: string;
  request: ChangeUserLoginEmailRequest;
}

/**
 * Hands an account over by changing its login email; with resetCredentials the
 * password and social sign-ins are wiped and the new address gets a
 * set-your-password link.
 */
export const changeUserLoginEmail = createAsyncThunk<
  ChangeUserLoginEmailResponse,
  ChangeUserLoginEmailArgs,
  { rejectValue: ApiErrorResponse }
>("admin/changeUserLoginEmail", async ({ instructorId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<ChangeUserLoginEmailResponse>(
      `${BASE}/${instructorId}/login-email`,
      request
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to change the login email"));
  }
});

export interface SetCustomPriceArgs {
  instructorId: string;
  /** The override to set, or null to clear it back to list pricing. */
  request: SetCustomPriceRequest | null;
}

export interface CustomPriceUpdate {
  instructorId: string;
  customMonthlyAmountMinorUnits: number | null;
  customAnnualAmountMinorUnits: number | null;
  customPriceCurrency: string | null;
}

/**
 * Sets or clears the per-account subscription price override — flexible,
 * individually negotiated pricing for hand-managed deals.
 */
export const setUserCustomPrice = createAsyncThunk<
  CustomPriceUpdate,
  SetCustomPriceArgs,
  { rejectValue: ApiErrorResponse }
>("admin/setCustomPrice", async ({ instructorId, request }, { rejectWithValue }) => {
  try {
    const response = request
      ? await axiosInstance.put<SubscriptionStatusResponse>(
          `${BASE}/${instructorId}/custom-price`,
          request
        )
      : await axiosInstance.delete<SubscriptionStatusResponse>(
          `${BASE}/${instructorId}/custom-price`
        );
    return {
      instructorId,
      customMonthlyAmountMinorUnits: response.data.customMonthlyAmountMinorUnits,
      customAnnualAmountMinorUnits: response.data.customAnnualAmountMinorUnits,
      customPriceCurrency: response.data.customPriceCurrency,
    };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to update the custom price"));
  }
});

const REFERRALS_BASE = "/admin/referrals/partners";

export interface FetchReferralPartnersArgs {
  page?: number;
  pageSize?: number;
}

export interface PaginatedReferralPartners {
  items: ReferralPartner[];
  page: number;
  totalCount: number;
  totalPages: number;
}

/** Active referral partners with aggregated totals, newest first. */
export const fetchReferralPartners = createAsyncThunk<
  PaginatedReferralPartners,
  FetchReferralPartnersArgs | void,
  { rejectValue: ApiErrorResponse }
>("admin/fetchReferralPartners", async (args, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<PaginatedAdminReferralPartnerResult>(REFERRALS_BASE, {
      params: {
        page: (args && args.page) ?? 1,
        pageSize: (args && args.pageSize) ?? 20,
      },
    });
    return {
      items: response.data.items.map(mapResponseToReferralPartner),
      page: response.data.page,
      totalCount: response.data.totalCount,
      totalPages: response.data.totalPages,
    };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load referral partners"));
  }
});

/** Creates a partner with a fresh referral code and terms-acceptance link. */
export const createReferralPartner = createAsyncThunk<
  ReferralPartner,
  CreateReferralPartnerRequest,
  { rejectValue: ApiErrorResponse }
>("admin/createReferralPartner", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<AdminReferralPartnerResponse>(
      REFERRALS_BASE,
      request
    );
    return mapResponseToReferralPartner(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to create the referral partner"));
  }
});

export interface ReferralPartnerDetail {
  partner: ReferralPartner;
  referrals: ReferralEntry[];
  payoutThresholdMinorUnits: number;
}

const mapDetail = (response: AdminReferralPartnerDetailResponse): ReferralPartnerDetail => ({
  partner: mapResponseToReferralPartner(response.partner),
  referrals: response.referrals.map(mapResponseToReferralEntry),
  payoutThresholdMinorUnits: response.payoutThresholdMinorUnits,
});

/** A partner with their full referral list (instructor identities joined in). */
export const fetchReferralPartnerDetail = createAsyncThunk<
  ReferralPartnerDetail,
  string,
  { rejectValue: ApiErrorResponse }
>("admin/fetchReferralPartnerDetail", async (partnerId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<AdminReferralPartnerDetailResponse>(
      `${REFERRALS_BASE}/${partnerId}`
    );
    return mapDetail(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load the referral partner"));
  }
});

/** Settles the partner's accrued bounties (every converted, unpaid referral). */
export const markReferralPartnerPaid = createAsyncThunk<
  ReferralPartnerDetail,
  string,
  { rejectValue: ApiErrorResponse }
>("admin/markReferralPartnerPaid", async (partnerId, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<AdminReferralPartnerDetailResponse>(
      `${REFERRALS_BASE}/${partnerId}/mark-paid`
    );
    return mapDetail(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to mark the bounties paid"));
  }
});

/** Soft-deletes the partner: their code stops attributing; history stays. */
export const deleteReferralPartner = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorResponse }
>("admin/deleteReferralPartner", async (partnerId, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`${REFERRALS_BASE}/${partnerId}`);
    return partnerId;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to remove the referral partner"));
  }
});

export interface FetchAdminFeedbackArgs {
  page?: number;
  pageSize?: number;
}

export interface PaginatedAdminFeedback {
  items: AdminFeedbackEntry[];
  page: number;
  totalCount: number;
  totalPages: number;
}

/** The cross-account feedback list (wishes, issues, notes), newest first. */
export const fetchAdminFeedback = createAsyncThunk<
  PaginatedAdminFeedback,
  FetchAdminFeedbackArgs | void,
  { rejectValue: ApiErrorResponse }
>("admin/fetchFeedback", async (args, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<PaginatedAdminFeedbackResult>("/admin/feedback", {
      params: {
        page: (args && args.page) ?? 1,
        pageSize: (args && args.pageSize) ?? 20,
      },
    });
    return {
      items: response.data.items.map(mapResponseToAdminFeedbackEntry),
      page: response.data.page,
      totalCount: response.data.totalCount,
      totalPages: response.data.totalPages,
    };
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load feedback"));
  }
});

/** The acquisition funnel (sign-up screen arrivals, Meta-ad arrivals, accounts created) over the trailing days. */
export const fetchAdminAcquisition = createAsyncThunk<
  AdminAcquisition,
  { days: number },
  { rejectValue: ApiErrorResponse }
>("admin/fetchAcquisition", async ({ days }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<AdminAcquisitionResponse>("/admin/acquisition", {
      params: { days },
    });
    return mapResponseToAdminAcquisition(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load the acquisition funnel"));
  }
});
